import React, {
  FC,
  Key,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import {
  Avatar,
  Button,
  Checkbox,
  ColorNameType,
  EnhancedFilterDropdown,
  Icon,
  Input,
  ListingImage,
  Pagination,
  TableProps,
  Tag,
  Text,
  theme,
} from 'components';
import { addDatacyToCell, addDatacyToTableHeader, capitalize } from 'utils';
import styled from 'styled-components';
import { debounce, difference, uniq, xor } from 'lodash';
import moment from 'moment';
import {
  SupraLockboxActivities,
  SupraActivityCodeToDescriptionMap,
  ESLockboxActivity,
  MasterLockOpenLockboxEvent,
  MasterLockOpenShackleEvent,
  buildMasterLockActivityDescription,
  SupraSystemSubscriptionType,
  JsonObject,
} from '@bb/domain/dist/types';
import Axios from 'axios';
import { getLockboxType } from '@bb/domain/dist/types/LockboxActivity';
import type {
  IUserGetFullUsersResponse,
  IUserSearchForMLSStaffResponse,
} from '@/app-package';
import { User } from '@bb/users';
import { Listing } from 'api/ListingController/getListing';
import { Loading } from '../../../app/react-common';
import { lockboxActivityIcons } from './types';
import { StyledTable } from '../../components';
import { getFullUsers } from '../../../api/UserController';
import { getListing } from '../../../api/ListingController';
import { LockboxesActivityFilterOptions } from '../../../api/get/getListingAccessActivities';
import SupraCoopTypeTag from '../MLSStaffPages/Tags/CoopTypeTag';

type ESLockboxListingAccessActivity = ESLockboxActivity & {
  activity:
  | SupraLockboxActivities
  | MasterLockOpenLockboxEvent
  | MasterLockOpenShackleEvent;
};

type LockboxActivityTableProps = {
  sortOptions: { sort: string; order: 'ascend' | 'descend' };
  filters: LockboxesActivityFilterOptions;
  setFilters: (filters: any) => void;
  setSortOptions: (sortOptions: any) => void;
  activities: any;
  loading?: boolean;
  page?: number;
  pageSize?: number;
  setPage?: (page: number) => void;
  totalCount?: number;
  tab?: 'User' | 'Lockboxes';
  // For ListingAccessActivityPage use
  listingAccessActivityProps?: { listingId: string };
};

let selectedIds: Key[] | null = null;

const LockboxActivityTable: FC<LockboxActivityTableProps> = ({
  sortOptions: { sort, order },
  filters,
  setFilters,
  setSortOptions,
  activities,
  loading = false,
  page = 1,
  pageSize = 20,
  totalCount = 0,
  setPage,
  tab = 'Lockboxes',
  listingAccessActivityProps,
}) => {
  const [filterLoading, setFilterLoading] = useState(false);

  const [userInput, setUserInput] = useState('');
  const [selectedUsers, setSelectedUsers] = useState<{
    [key: string]: JsonObject<User>;
  }>({});
  const [searchedUsers, setSearchedUsers] = useState<
    IUserSearchForMLSStaffResponse['users']
  >([]);
  const [searchedUsersCache, setSearchedUsersCache] = useState<
    IUserSearchForMLSStaffResponse['users']
  >([]);

  const [listingInput, setListingInput] = useState('');
  const [selectedListings, setSelectedListings] = useState<{
    [key: string]: Listing;
  }>({});
  const [searchedListings, setSearchedListings] = useState<Listing[]>([]);

  const initialFilters = useRef<LockboxesActivityFilterOptions>({});
  useEffect(() => {
    initialFilters.current = { ...filters };
  }, []);

  useEffect(() => {
    const newSearchedUsers = difference(
      searchedUsers.map((user: any) => user.id),
      searchedUsersCache.map((user: any) => user.id),
    );
    setSearchedUsersCache(prev => [
      ...prev,
      ...searchedUsers.filter(user => newSearchedUsers.includes(user.id)),
    ]);
  }, [searchedUsers]);

  const debouncedUserSearch = useCallback(
    debounce(async (value: string) => {
      const userSearchUrl = listingAccessActivityProps
        ? `/listing/${listingAccessActivityProps.listingId}/access_activity/users`
        : '/supra/lockbox/activities/users';

      if (!value) {
        setSearchedUsers([]);
        return;
      }
      setFilterLoading(true);
      const { data } = await Axios.get(userSearchUrl, {
        params: {
          lockboxIds: filters.lockboxIds,
          ownerOrAssignee: filters.ownerOrAssignee,
          userIds:
            !filters.ownerOrAssignee?.length && !filters.lockboxIds?.length
              ? initialFilters.current.userIds || []
              : [],
          userFilterSearch: value,
        },
      });
      setSearchedUsers(data);
      setFilterLoading(false);
    }, 500),
    [filters],
  );

  const debouncedListingSearch = useCallback(
    debounce(async value => {
      if (!value) {
        setSearchedListings([]);
        return;
      }
      setFilterLoading(true);
      const { data } = await Axios.get('/supra/lockbox/activities/listings', {
        params: {
          lockboxIds: filters.lockboxIds,
          ownerOrAssignee: filters.ownerOrAssignee,
          userIds:
            !filters.ownerOrAssignee?.length && !filters.lockboxIds?.length
              ? initialFilters.current.userIds || []
              : [],
          listingFilterSearch: value,
        },
      });
      setSearchedListings(data);
      setFilterLoading(false);
    }, 500),
    [filters],
  );

  const resetUsers = () => {
    selectedIds = [...(initialFilters.current.userIds || [])];
    setFilters({ ...filters, userIds: initialFilters.current.userIds || [] });
  };
  const resetListings = () => {
    selectedIds = [...(initialFilters.current.listingIds || [])];
    setFilters({
      ...filters,
      listingIds: initialFilters.current.listingIds || [],
    });
  };

  useEffect(() => {
    debouncedUserSearch(userInput);
  }, [debouncedUserSearch, userInput]);
  useEffect(() => {
    debouncedListingSearch(listingInput);
  }, [debouncedListingSearch, listingInput]);

  // cleanup
  useEffect(() => () => {
    debouncedUserSearch.cancel();
    debouncedListingSearch.cancel();
  }, [debouncedUserSearch, debouncedListingSearch]);

  useEffect(() => {
    const getSelectedUsers = async () => {
      const existingUsers = Object.keys(selectedUsers);
      // find new user ids not in cache
      const usersToFetch = difference(filters.userIds, [
        ...existingUsers,
        ...searchedUsersCache.map((user: any) => user.id),
      ]);

      let fetchedUsers: IUserGetFullUsersResponse = [];
      if (usersToFetch.length) {
        fetchedUsers = await getFullUsers({ userIds: usersToFetch });
      }

      const newSelectedUsers = (filters.userIds || []).reduce<
        Record<string, any>
      >((acc, userId) => {
        acc[userId] =
          selectedUsers[userId] ||
          fetchedUsers.find(_user => userId === _user.id) ||
          searchedUsersCache.find(_user => userId === _user.id);
        return acc;
      }, {});

      setSelectedUsers(newSelectedUsers);
    };
    const getSelectedListings = async () => {
      const existingListings = Object.keys(selectedListings);
      const listingsToFetch = difference(
        filters.listingIds,
        existingListings,
      ).filter(listing => listing !== 'na');
      const listingToRemove = difference(
        existingListings,
        filters.listingIds || [],
      );
      const fetchedListings = await Promise.all(
        listingsToFetch.map(id => getListing(id)),
      );
      const temp = { ...selectedListings };
      listingToRemove.forEach(listingId => delete temp[listingId]);
      fetchedListings.forEach(listing => {
        temp[listing.id] = listing;
      });
      setSelectedListings(temp);
    };
    if (xor(Object.keys(selectedUsers), filters.userIds).length) {
      getSelectedUsers();
    }
    if (xor(Object.keys(selectedListings), filters.listingIds).length) {
      getSelectedListings();
    }
  }, [filters.userIds, filters.listingIds]);

  const columns: TableProps['columns'] = ([
    {
      title: 'Activity',
      dataIndex: 'activity',
      key: 'activity',
      width: 250,
      render: (
        _,
        {
          activity,
          brand,
          owner,
          ownerType,
          assignee,
          assigneeType,
          listing,
          transDate,
        }: ESLockboxListingAccessActivity,
      ) => {
        const user =
          activity === 'assignee_changed' && assigneeType === 'user'
            ? assignee
            : activity === 'owner_changed' && ownerType === 'user'
              ? owner
              : undefined;
        const org =
          activity === 'assignee_changed' && assigneeType !== 'user'
            ? assignee
            : activity === 'owner_changed' && ownerType !== 'user'
              ? owner
              : undefined;
        const unassignedListing =
          activity === 'unassigned_from_listing' ? listing : undefined;
        const type = activity === 'assignee_changed' ? assigneeType : ownerType;
        return (
          <StyledActivity>
            {lockboxActivityIcons[activity] && (
              <StyledIconWrapper color={lockboxActivityIcons[activity].color}>
                <Icon
                  type={lockboxActivityIcons[activity].icon}
                  color={lockboxActivityIcons[activity].color}
                  width={14}
                />
              </StyledIconWrapper>
            )}
            <InnerWrapper>
              <Text strong>
                {brand === 'Supra'
                  ? SupraActivityCodeToDescriptionMap[
                  activity as SupraLockboxActivities
                  ]
                  : buildMasterLockActivityDescription(
                    activity as
                    | MasterLockOpenLockboxEvent
                    | MasterLockOpenShackleEvent,
                  )}
              </Text>
              {user ? (
                <InnerAvatarWrapper style={{ marginLeft: '5px' }}>
                  <Avatar user={user} width={20} /> {user?.firstName || ''}{' '}
                  {user?.lastName || ''}
                </InnerAvatarWrapper>
              ) : org ? (
                <InnerAvatarWrapper style={{ marginLeft: '5px' }}>
                  <AvatarContainer>
                    <Avatar
                      user={{
                        ...org,
                        firstName: org.name,
                        /* @ts-ignore  */
                        avatarFilestackId: org.logoUrl ?? org.logo,
                      }}
                      width={20}
                    />
                  </AvatarContainer>{' '}
                  {`${org?.name}` || ''}
                  <StyledType>
                    {capitalize(type === 'org' ? 'brokerage' : type!)}
                  </StyledType>
                </InnerAvatarWrapper>
              ) : unassignedListing ? (
                <StyledListingContainer>
                  <ListingImage
                    imageSrc={unassignedListing.thumbnail}
                    size={20}
                  />
                  <Text style={{ fontWeight: 500 }}>
                    {unassignedListing.line1}
                  </Text>{' '}
                  <Text
                    color=" rgba(0, 0, 0, 0.45)"
                    style={{ fontWeight: 400 }}
                  >
                    {unassignedListing.line2}
                  </Text>
                </StyledListingContainer>
              ) : null}
              <Text color=" rgba(0, 0, 0, 0.65)" dataCy="lockbox-trans-date">
                {moment(transDate).format('MMM D, YYYY')}
              </Text>
              <Text color=" rgba(0, 0, 0, 0.65)" dataCy="lockbox-trans-time">
                {moment(transDate).format('h:mm:ss A')}
              </Text>
            </InnerWrapper>
          </StyledActivity>
        );
      },
      onCell: addDatacyToCell('lockbox-activity-name'),
      onHeaderCell: addDatacyToTableHeader('lockbox-activity-name'),
      filteredValue: filters.activities?.map(
        key => SupraActivityCodeToDescriptionMap[key],
      ),
      filterDropdown: props => (
        <EnhancedFilterDropdown {...props} okText="Apply" resetText="Reset" />
      ),
      filters: uniq(
        Object.values(SupraActivityCodeToDescriptionMap).filter(activity =>
          // Only 'Lockbox opened' and 'Shackle released' are shown on Listing Access Activity mode
          listingAccessActivityProps
            ? [
              SupraActivityCodeToDescriptionMap['00'],
              SupraActivityCodeToDescriptionMap['01'],
            ].includes(activity)
            : true,
        ),
      )
        .map((key: SupraLockboxActivities) => ({
          text: capitalize(key),
          value: key,
        }))
        .sort((a, b) =>
          a.value.toLowerCase() < b.value.toLowerCase() ? -1 : 1,
        ),
      filterIcon: () => (
        <Icon
          width={12}
          type="antdFilterFilled"
          color={filters.activities?.length ? 'primary' : undefined}
        />
      ),
    },
    {
      title: 'Type',
      dataIndex: 'productCode',
      key: 'productCode',
      onCell: addDatacyToCell('lockbox-serial-type'),
      onHeaderCell: addDatacyToTableHeader('lockbox-serial-type'),
      render: (_, { brand, productCode }) => (
        <Text style={{ wordBreak: 'keep-all' }}>
          {getLockboxType(brand, productCode)}
        </Text>
      ),
    },
    {
      title: 'Lockbox Serial #',
      dataIndex: 'lockboxSerialNumber',
      key: 'lockboxSerialNumber',
      onCell: addDatacyToCell('lockbox-serial-number'),
      onHeaderCell: addDatacyToTableHeader('lockbox-serial-number'),
    },
    {
      title: 'Key Type & Serial #',
      dataIndex: 'keyType',
      key: 'keyType',
      render: (
        _,
        { brand, keyType, keySerialNumber, userSubRole, coopType },
      ) => brand === 'Supra' ? (
        <>
          {keyType ? (
            <Tag
              color={
                keyType === SupraSystemSubscriptionType.LIMITED
                  ? 'blue'
                  : keyType === SupraSystemSubscriptionType.MANAGED_ACCESS
                    ? 'orange'
                    : 'green'
              }
            >
              {capitalize(keyType.replace('_', ' '))}
              {keyType !== SupraSystemSubscriptionType.FULL && userSubRole
                ? ` - ${userSubRole.split('/').map(capitalize).join('/')}`
                : ''}
            </Tag>
          ) : null}
          {coopType && <SupraCoopTypeTag coopType={coopType} />}
          <StyledText style={{ marginTop: '5px' }}>
            {keySerialNumber || null}
          </StyledText>
        </>
      ) : null,
      onCell: addDatacyToCell('lockbox-activity-key-type'),
      onHeaderCell: addDatacyToTableHeader('lockbox-activity-key-type'),
      filteredValue: filters.keyTypes,
      filters: Object.keys(SupraSystemSubscriptionType).map((key: string) => ({
        text: capitalize(key.replace('_', ' ')),
        value: key,
      })),
      filterDropdown: props => (
        <EnhancedFilterDropdown {...props} okText="Apply" resetText="Reset" />
      ),
      filterIcon: () => (
        <Icon
          width={12}
          type="antdFilterFilled"
          color={filters.keyTypes?.length ? 'primary' : undefined}
        />
      ),
    },
    {
      title: 'User Information',
      dataIndex: 'user',
      key: 'user',
      onCell: addDatacyToCell('lockbox-activity-user'),
      onHeaderCell: addDatacyToTableHeader('lockbox-activity-user'),
      filterIcon: () => (
        <Icon
          width={12}
          type="antdFilterFilled"
          color={filters.userIds?.length ? 'primary' : undefined}
        />
      ),
      defaultFilteredValue: [],
      filteredValue: filters.userIds,
      onFilterDropdownOpenChange: () => setUserInput(''),
      filterDropdown: ({ confirm, setSelectedKeys, selectedKeys }) => (
        <div style={{ padding: 8 }}>
          <Input
            placeholder={`Search Users`}
            onChange={element => setUserInput(element.target.value)}
            value={userInput}
            allowClear
            disabled={tab === 'User'}
          />
          {filterLoading && <Loading />}
          {searchedUsers.length
            ? searchedUsers.map(searchedUser => (
              <StyledFilterUser
                key={searchedUser.id}
                user={searchedUser}
                selectedKeys={selectedKeys}
                setSelectedKeys={setSelectedKeys}
                disabled={tab === 'User'}
              />
            ))
            : Object.values(selectedUsers).map(selectedUser => (
              <StyledFilterUser
                key={selectedUser.id}
                user={selectedUser}
                selectedKeys={selectedKeys}
                setSelectedKeys={setSelectedKeys}
                disabled={tab === 'User'}
              />
            ))}
          <StyledFilterBottom>
            <Button
              size="small"
              type="link"
              disabled={!selectedKeys.length || tab === 'User'}
              onClick={() => {
                setSelectedKeys([]);
              }}
            >
              Reset
            </Button>
            <Button
              type="primary"
              onClick={() => {
                confirm({ closeDropdown: true });
                setSearchedUsers([]);
                setUserInput('');
              }}
              size="small"
              disabled={tab === 'User'}
            >
              Apply
            </Button>
          </StyledFilterBottom>
        </div>
      ),
      render: (
        _,
        record: {
          user: any;
          isBrokerBot: boolean;
          supraCrossPlatformUser: string;
        },
      ) => {
        if (record.isBrokerBot && !record.supraCrossPlatformUser) {
          return (
            <>
              <StyledUserNameContainer>
                <Avatar user={{ firstName: 'Broker', lastName: 'Bot' }} />
                Broker Bot
              </StyledUserNameContainer>
            </>
          );
        }
        if (record.isBrokerBot && record.supraCrossPlatformUser) {
          return (
            <>
              <StyledUserNameContainer>
                <Avatar
                  user={{
                    firstName: record.supraCrossPlatformUser,
                    lastName: '',
                  }}
                />
                {record.supraCrossPlatformUser}
              </StyledUserNameContainer>
            </>
          );
        }
        return (
          <>
            <StyledUserNameContainer>
              <Avatar user={record.user} /> {record.user.firstName}{' '}
              {record.user.lastName}
            </StyledUserNameContainer>
            <StyledText>ID: {record.user.mlsNumbers || '-'}</StyledText>
            <StyledText>Email: {record.user.email || '-'}</StyledText>
            <StyledText>Phone: {record.user.phone || '-'}</StyledText>
          </>
        );
      },
    },
    {
      title: 'Office Information',
      dataIndex: 'officeInformation',
      key: 'officeInformation',
      onCell: addDatacyToCell('office-information'),
      onHeaderCell: addDatacyToTableHeader('office-information'),
      render: (_, { user, owner, assignee, assigneeType, ownerType } = {} as any) => {
        if (!user?.org) return null;
        const officeName = assigneeType === 'office' ? assignee?.officeName
          : ownerType === 'office' ? owner?.officeName
          : "";
        return (
          <>
            {officeName && officeName !== "" && <StyledText>{officeName}</StyledText>}
            <StyledText>{user.org?.name || '-'}</StyledText>
            <StyledText>ID: {user.org?.oid || '-'}</StyledText>
            <StyledText>Phone: {user.org?.phone || '-'}</StyledText>
          </>
        );
      },
    },
    !listingAccessActivityProps && {
      title: 'Listing Information',
      dataIndex: 'listing',
      key: 'listing',
      render: (_, { listing, activity }: ESLockboxActivity) => {
        if (!listing || activity === 'unassigned_from_listing') return null;
        return (
          <>
            {listing.mlsNumbers ? (
              <StyledText>MLS: {listing.mlsNumbers}</StyledText>
            ) : null}
            <StyledText>{`${listing.line1} ${listing.line2 || ''}`}</StyledText>
            <StyledText>
              {`${listing.city}, ${listing.province}, ${listing.postal_code}`}
            </StyledText>
          </>
        );
      },
      onCell: addDatacyToCell('lockbox-activity-listing'),
      onHeaderCell: addDatacyToTableHeader('lockbox-activity-listing'),
      filteredValue: filters.listingIds,
      onFilterDropdownOpenChange: () => setListingInput(''),
      filterDropdown: ({ confirm, setSelectedKeys, selectedKeys }) => (
        <div style={{ padding: 8 }}>
          <Input
            placeholder={`Search Listings`}
            onChange={element => setListingInput(element.target.value)}
            value={listingInput}
            allowClear
          />
          {filterLoading && <Loading />}
          {searchedListings?.length
            ? searchedListings.map(searchedListing => (
              <StyledFilterListing
                key={searchedListing.id}
                listing={searchedListing}
                selectedKeys={selectedKeys}
                setSelectedKeys={setSelectedKeys}
              />
            ))
            : Object.values(selectedListings).map(selectedListing => (
              <StyledFilterListing
                key={selectedListing.id}
                listing={selectedListing}
                selectedKeys={selectedKeys}
                setSelectedKeys={setSelectedKeys}
              />
            ))}
          <div>
            <Checkbox
              checked={selectedKeys.includes('na')}
              value={'n/a'}
              onChange={() => {
                selectedIds = selectedKeys.includes('na')
                  ? selectedKeys.filter((key: string) => key !== 'na')
                  : [...selectedKeys, 'na'];
                setSelectedKeys([...selectedIds]);
              }}
            />{' '}
            N/A
          </div>
          <StyledFilterBottom>
            <Button
              size="small"
              type="link"
              disabled={!selectedKeys?.length}
              onClick={() => {
                setSelectedKeys([]);
              }}
            >
              Reset
            </Button>
            <Button
              type="primary"
              onClick={() => {
                confirm({ closeDropdown: true });
                setSearchedListings([]);
                setListingInput('');
              }}
              size="small"
            >
              Apply
            </Button>
          </StyledFilterBottom>
        </div>
      ),
      filterIcon: () => (
        <Icon
          width={12}
          type="antdFilterFilled"
          color={filters.listingIds?.length ? 'primary' : undefined}
        />
      ),
    },
  ] as TableProps['columns'])!.filter(Boolean);

  return (
    <div>
      <StyledSearchTable
        columns={columns}
        dataSource={activities}
        rowKey="_id"
        scroll={{ x: 'auto' }}
        loading={loading}
        pagination={false}
        onChange={(_, tableFilters, sorter: any) => {
          setFilters((prevFilters: LockboxesActivityFilterOptions) => ({
            ...prevFilters,
            activities: tableFilters?.activity?.reduce(
              (acc, value) => [
                ...acc,
                ...Object.keys(SupraActivityCodeToDescriptionMap).filter(
                  (key: SupraLockboxActivities) =>
                    SupraActivityCodeToDescriptionMap[key] === value,
                ),
              ],
              [],
            ),
            lockboxSerialNumbers: tableFilters?.lockboxSerialNumbers,
            userIds: tableFilters?.user || [],
            listingIds: tableFilters?.listing || [],
            keyTypes: tableFilters?.keyType,
          }));
          if (sorter?.columnKey && sorter.order) {
            setSortOptions({ sort: sorter.columnKey, order: sorter.order });
          }
          setPage!(1);
        }}
      />
      <StyledPagination
        current={page}
        defaultPageSize={pageSize}
        total={Math.max(totalCount, activities?.length)}
        showSizeChanger={false}
        onChange={_page => setPage && setPage(_page)}
        // add dataCy to each pagination element
        itemRender={(tablePage, type, element: any) => ({
          ...element,
          props: {
            ...element.props,
            'data-cy': `${tablePage}-${type}-${page}`,
          },
        })}
      />
    </div>
  );
};

export default LockboxActivityTable;

export const StyledFilterUser = ({
  user,
  selectedKeys,
  setSelectedKeys,
  hideAvatar = false,
  disabled,
  allKeys,
}: {
  user: { id: string; name?: string; firstName?: string; lastName?: string };
  selectedKeys: Key[];
  setSelectedKeys: (value: Key[]) => void;
  hideAvatar?: boolean;
  disabled?: boolean;
  allKeys?: Key[];
}) => (
  <StyledFilterSearchResult>
    <Checkbox
      checked={selectedKeys.includes(user.id)}
      value={user.id}
      onChange={() => {
        if (allKeys && user.id === 'All') {
          setSelectedKeys(
            selectedKeys.length === allKeys.length ? [] : [...allKeys],
          );
        } else {
          selectedIds = selectedKeys.includes(user.id)
            ? selectedKeys.filter(
              (key: string) => key !== user.id && key !== 'All',
            )
            : [...selectedKeys, user.id];
          setSelectedKeys([...selectedIds]);
        }
      }}
      disabled={disabled}
    />
    <Text>
      {!hideAvatar && (
        <Avatar
          user={user.firstName ? user : { ...user, firstName: user.name }}
        />
      )}
      {user.name || `${user.firstName} ${user.lastName}`}
    </Text>
  </StyledFilterSearchResult>
);

export const StyledFilterListing = ({
  listing,
  selectedKeys,
  setSelectedKeys,
}: {
  listing: {
    id: string;
    thumbnail?: string;
    line1: string;
    line2?: string;
    city: string;
    province: string;
  };
  selectedKeys: Key[];
  setSelectedKeys: (value: Key[]) => void;
}) => (
  <StyledFilterSearchResult>
    <Checkbox
      checked={selectedKeys.includes(listing.id)}
      value={listing.id}
      onChange={() => {
        selectedIds = selectedKeys.includes(listing.id)
          ? selectedKeys.filter((key: string) => key !== listing.id)
          : [...selectedKeys, listing.id];
        setSelectedKeys([...selectedIds]);
      }}
    />
    {listing.id !== 'na' && (
      <StyledListingContainer>
        <ListingImage imageSrc={listing.thumbnail} size={30} />
        <Text style={{ fontWeight: 500 }}>{listing.line1}</Text>{' '}
        <Text color=" rgba(0, 0, 0, 0.45)" style={{ fontWeight: 400 }}>
          {listing.line2}
        </Text>
        <Text>
          {listing.city}, {listing.province}
        </Text>
      </StyledListingContainer>
    )}
  </StyledFilterSearchResult>
);

const StyledSearchTable = styled(StyledTable)`
  .ant-table-filter-trigger.active .anticon-filter {
    color: ${theme.blueBase};
  }
`;

const StyledPagination = styled(Pagination)`
  border-top: 1px solid ${theme.gray3};
  background-color: white;
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 10px;
`;

const StyledActivity = styled.div`
  display: flex;
  align-items: center;
`;

const InnerWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;
const InnerAvatarWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 5px;
  font-size: 0.9em;
`;

const StyledIconWrapper = styled.div<{ color: ColorNameType }>`
  display: flex;
  align-items: center;
  justify-content: center;
  ${({ color }) => `background-color: ${theme.getColor(color)}19;`}
  padding:8px;
  margin: 0 5px;
  border-radius: 50%;
`;

const StyledListingContainer = styled.div`
  display: flex;
  justify-content: flex-start;
  align-items: center;
  flex-wrap: wrap;
  max-width: 300px;
  gap: 3px;
`;

export const StyledFilterSearchResult = styled.div`
  display: flex;
  justify-content: flex-start;
  align-items: center;
  gap: 8px;
  margin: 5px 0;
  min-height: 30px;
`;
export const StyledFilterBottom = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-around;
  margin-top: 10px;
`;

const StyledUserNameContainer = styled.div`
  margin-bottom: 5px;
`;

const StyledText = styled(Text)`
  display: block;
`;

const StyledType = styled(Text)`
  margin-left: 2px;
  color: ${theme.gray6};
  font-weight: 500;
  white-space: nowrap;
`;

const AvatarContainer = styled.div`
  min-width: 20px;
`;
